import type { CloudLinkStatus } from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import { Cloud, LoaderCircle, LogIn } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { loadCloudLink, notifyCloudLinkChanged, startCloudLink } from '@/lib/cloud-link';
import { errorMessage } from '@/lib/error-message';

const POLL_MS = 2_000;

/**
 * A machine's Crowdex needs its Crowdex Cloud account: the account's settings, plugins and
 * connections live there. Until the link exists, the app shows how to sign in instead of itself.
 * A server without a cloud (`CROWDEX_CLOUD_URL` empty) runs alone.
 */
export function CloudLinkRequired({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<CloudLinkStatus | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await loadCloudLink();
      setStatus(next);
      if (next.account !== null) {
        setSigningIn(false);
        notifyCloudLinkChanged();
      }
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The approval happens in the browser: follow it until the link is saved.
  useEffect(() => {
    if (!signingIn) return;
    const poll = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(poll);
  }, [signingIn, refresh]);

  if (status === null && error === null)
    return (
      <main className="grid min-h-dvh place-items-center text-muted-foreground text-sm">
        Loading Crowdex…
      </main>
    );
  if (status !== null && (status.url === null || status.account !== null)) return children;

  const host = status?.url == null ? 'Crowdex Cloud' : new URL(status.url).host;
  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <section aria-labelledby="cloud-link-required-title" className="w-full max-w-sm space-y-5">
        <div className="flex size-10 items-center justify-center rounded-full bg-muted">
          <Cloud aria-hidden className="size-5" />
        </div>
        <div className="space-y-2">
          <h1 className="type-section-title" id="cloud-link-required-title">
            Sign in to Crowdex Cloud
          </h1>
          <p className="text-muted-foreground text-sm">
            Crowdex keeps your account, its plugins and its connections on {host}. Sign in there
            once from your browser; your conversations keep running on this computer.
            {status?.machineName
              ? ` Your account lists it as “${status.machineName}”, the name it sends when it links.`
              : ''}
          </p>
        </div>
        {error !== null && <p className="text-destructive text-sm">{error}</p>}
        <Button
          className="w-full"
          disabled={signingIn}
          onClick={() => {
            setError(null);
            void startCloudLink()
              .then(() => setSigningIn(true))
              .catch((cause: unknown) => setError(errorMessage(cause)));
          }}
        >
          {signingIn ? (
            <LoaderCircle aria-hidden className="size-4 animate-spin" />
          ) : (
            <LogIn aria-hidden className="size-4" />
          )}
          {signingIn ? 'Waiting for the browser…' : `Sign in to ${host}`}
        </Button>
      </section>
    </main>
  );
}
